import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { Box, Flex, Button, Icon, Spinner } from "@chakra-ui/react";
import { FaDesktop, FaMobileAlt } from "react-icons/fa";
import { useEffect, useRef } from "react";
import { useState } from "react";

const CreateCampaignPreview = ({
  showPreview,
  emailHtml,
  view,
  setView,
  isMobile,
  loading = false,
}: {
  showPreview: boolean;
  loading?: boolean;
  emailHtml: string;
  view: "desktop" | "mobile";
  setView: (view: "desktop" | "mobile") => void;
  isMobile: boolean;
}) => {
  const isMobilePreviewOn = showPreview && isMobile;
  const ref = useRef<HTMLIFrameElement>(null);
  const emailPreviewWidth =
    view === "desktop" ? "100%" : isMobile ? "100%" : "480px";
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    const tryPostMessage = () => {
      const iframe = ref.current;
      if (!iframe || !iframe.contentWindow) {
        // If iframe or contentWindow is not available, try again after 50ms
        const retryTimeout = setTimeout(tryPostMessage, 50);
        return () => clearTimeout(retryTimeout);
      }

      const duration = isRendered ? 0 : 50;
      const timeout = setTimeout(() => {
        iframe.contentWindow?.postMessage(
          { type: "html", value: emailHtml },
          "*"
        );
      }, duration);
      setIsRendered(true);
      return () => clearTimeout(timeout);
    };

    const cleanup = tryPostMessage();
    return cleanup;
  }, [emailHtml, isRendered]);

  useEffect(() => {
    setIsRendered(false);
  }, [showPreview, isMobile]);

  const initialSrcDoc = ` 
    <html>
      <head>
        <script>
          window.addEventListener('message', (event) => {
            const { type, value } = event.data;
            if (type === 'html') {
              document.body.innerHTML = value;
            }
          });
        </script>
      </head>
      <body style="height: 100%;"></body>
    </html>
  `;

  return (
    <Box flex="1" bg="#C9C9C9" w="100%" h="100vh" overflow={"hidden"}>
      <AnimatePresence>
        {(showPreview || !isMobile) && (
          <motion.div
            initial={{ y: isMobile ? "100%" : 0, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: isMobile ? "100%" : 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: isMobile ? "fixed" : "static",
              bottom: 0,
              height: "100%",
              left: 0,
              right: 0,
              zIndex: 999,
              backgroundColor: isMobilePreviewOn ? "white" : "transparent",
            }}
          >
            <Flex p={4} justifyContent="center" alignItems="center">
              <Flex justifyContent="center" gap={2}>
                <Button
                  variant={view === "desktop" ? "solid" : "outline"}
                  onClick={() => setView("desktop")}
                >
                  <Icon as={FaDesktop} />
                </Button>
                <Button
                  variant={view === "mobile" ? "solid" : "outline"}
                  onClick={() => setView("mobile")}
                >
                  <Icon as={FaMobileAlt} />
                </Button>
              </Flex>
            </Flex>
            <Box
              borderTop="1px solid"
              borderColor="black"
              borderTopColor="blackAlpha.400"
            />
            <Box
              px={isMobile ? 0 : 12}
              py={isMobile ? 0 : 6}
              display="flex"
              justifyContent="center"
              height="100%"
            >
              <Box
                borderRadius="10px !important"
                overflow="hidden"
                backgroundColor="white"
                boxShadow="0px 0px 15px rgba(0, 0, 0, 0.05)"
                width={emailPreviewWidth}
                position="relative"
              >
                {loading ? (
                  <Box
                    display="flex"
                    w="100%"
                    position="absolute"
                    h="100%"
                    justifyContent="center"
                    alignItems="center"
                    backgroundColor="rgba(255, 255, 255, 0.5)"
                  >
                    <Spinner />
                  </Box>
                ) : null}
                <iframe
                  ref={ref}
                  srcDoc={initialSrcDoc}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    border: "none",
                  }}
                  title="Live Preview"
                />
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default CreateCampaignPreview;
